import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";
import {handleAjaxSuccess} from "../../../helpers/swalHelper";
import {getSingleInputElementByNameV3} from "../../../helpers/forms/formInputsHelpers";
import {waitUntil} from "../../../helpers/waitUntil";
import {
    displayLocalValidationError,
    displayLocalValidationErrorPromise
} from "../../../helpers/forms/validationErrors/utils";
import {recoverSharedVisitorTypeUnavailable} from "../sharedInventory/sharedVisitorTypeUnavailable";

class OrderSummaryStage extends BaseStage {
    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getViewRoute;

        this.cancelAwaitingTransactionFlag = false;

        loading();

        Axios.post(
            getViewHtmlRoute,
            this.globalState.getOrderDataForServer(),
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                if (this.recoverSharedVisitorTypeUnavailable(error)) {
                    return;
                }

                if (this.handleSpecialErrors(error)) {
                    return;
                }

                handleAjaxErrorV2(error)

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('summary');
            })
            .finally(() => {
                // Keep loading overlay displayed in express mode as express form will take care of
                // hiding it after full checkout
                if (this.globalState.isExpressMode()) {
                    return;
                }
                stopLoading()
            })
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'summary';
    }

    display() {
        super.display();

        this.syncCustomerSpecialRequestsInput();
    }

    initView() {
        this.placeOrderRoute = this.domContainer.dataset.placeOrderRoute;

        this.totalPreviewComponent = this.getTotalPreviewComponent()

        this.totalPreviewComponent.withTOSWarning = true;

        // Fill in CC last four
        const lastFourContainer = this.domContainer.querySelector('.cc_last_four');
        if (lastFourContainer) {
            if (this.globalState.isUsingStripePaymentElements) {
                // Get CC last 4 number from stripe response
                try {
                    lastFourContainer.innerHTML = this.globalState.paymentInfo.stripe_confirmation_token_response.payment_method_preview.card.last4;
                } catch (error) {
                    console.error({lastFourError: error})
                    lastFourContainer.innerHTML = 'Unknown';
                }
            } else if (this.globalState.paymentInfo.cc_number) {
                // Default to manual cc number
                lastFourContainer.innerHTML = this.globalState.paymentInfo.cc_number.slice(-4);
            }
        }

        // Setup edit buttons
        this.domContainer.querySelectorAll('.change-payment-settings-button').forEach(button => {
            button.addEventListener('click', () => {
                this.globalState.stageManager.goToStage('payment-info');
            })
        })

        // Setup edit buttons
        this.domContainer.querySelectorAll('.change-order-details-button').forEach(button => {
            button.addEventListener('click', () => {
                this.globalState.stageManager.goToStage('seat-type-select');
            })
        })

        // Setup how did you find about us text
        const howDidYouFindUsSelect = getSingleInputElementByNameV3(this.domContainer, 'how_did_you_find_us_select');
        const howDidYouFindUsInput = getSingleInputElementByNameV3(this.domContainer, 'how_did_you_find_us_input');

        // Setup select change event
        howDidYouFindUsSelect.addEventListener('change', () => {
            if (howDidYouFindUsSelect.value === 'other') {
                // Show custom box
                howDidYouFindUsInput.classList.remove('d-none')
                // And clear its value
                howDidYouFindUsInput.value = '';
            } else {
                // Set predefined value
                howDidYouFindUsInput.value = howDidYouFindUsSelect.value;
                // And hide custom box
                howDidYouFindUsInput.classList.add('d-none')
            }

            // Trigger change event in input to update in memory
            howDidYouFindUsInput.dispatchEvent(new InputEvent('change'));
        })

        // Setup change even on input to sync value in memory
        howDidYouFindUsInput.addEventListener('change', () => {
            this.globalState.setHowDidYouFindUs(howDidYouFindUsInput.value);
        })

        const customerSpecialRequestsInput = getSingleInputElementByNameV3(
            this.domContainer,
            'customer_special_requests'
        );
        if (customerSpecialRequestsInput) {
            this.globalState.setCustomerSpecialRequests(customerSpecialRequestsInput.value);
            customerSpecialRequestsInput.addEventListener('input', () => {
                this.globalState.setCustomerSpecialRequests(customerSpecialRequestsInput.value);
            });
        }

        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click', e => {
                    e.stopPropagation();
                    e.preventDefault();

                    this.placeOrder();
                },
                {
                    capture: true,
                }
            )

        this.updateTotals()
            .then(() => {
                // Keep loading overlay displayed in express mode as express form will take care of
                // hiding it after full checkout
                if (this.globalState.isExpressMode()) {
                    return;
                }

                stopLoading()
            });

        // Wait a bit and enable next stage button
        const delayBeforeEnablingCheckout = this.globalState.isExpressMode() === false ?
            1000 : // Wait a bit for normal checkout
            32; // Enable immediately for express checkout

        setTimeout(() => {
            this.enableNextStageButton()
        }, delayBeforeEnablingCheckout)
    }

    syncCustomerSpecialRequestsInput() {
        const customerSpecialRequestsInput = getSingleInputElementByNameV3(
            this.domContainer,
            'customer_special_requests'
        );

        if (customerSpecialRequestsInput) {
            customerSpecialRequestsInput.value = this.globalState.customerSpecialRequests;
        }
    }

    getTotalPreviewComponent() {
        return new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState,
        );
    }

    updateTotals() {
        return this.totalPreviewComponent.updateTotalsFromServer();
    }

    placeOrder(skipTransaction = false) {
        const orderData = this.globalState.getOrderDataForServer(true);

        if (skipTransaction) {
            orderData.skipTransaction = skipTransaction;
        }

        loading();
        this.disableNextStageButton();

        Axios.post(
            this.placeOrderRoute,
            orderData,
        )
            .then(response => {
                // Let the user leave without warning
                window.onbeforeunload = undefined;

                this.handlePlaceOrderSuccess(response);
            })
            .catch(error => {
                const errorHandled = this.handleCustomErrors(error);

                if (errorHandled) {
                    // Custom error handled took care of this one
                    return;
                }

                // Enable button again
                this.enableNextStageButton();

                // Make sure the error appears as a swal popup
                handleAjaxErrorV2(error, createElement('<div></div>'))

                // Custom behavior for custom errors
                // First wait until the swal popup is visible
                setTimeout(() => {
                    // Then wait until it is gone
                    waitUntil(() => {
                        return document.querySelector('.swal2-html-container') === null
                    }).then(() => {
                        // Finally handle custom cases
                        const errors = error.response.data.errors;
                        const errorCode = Object.keys(errors)[0];
                        const errorText = this.getValidationErrorText(error);

                        if (errorCode === 'seats_no_longer_available') {
                            this.backToSeatSelectionStage(error);
                        }

                        if (errorCode === 'not_enough_seats_available' || errorText.includes('Not enough spots available')) {
                            this.backToTicketTypeSelectionStage();
                        }
                    })
                }, 256)
            })
            .finally(stopLoading)
    }

    handlePlaceOrderSuccess(response) {
        handleAjaxSuccess(response);
    }

    // noinspection JSUnusedLocalSymbols
    /**
     * Overridden by admin child
     *
     * @param error
     * @return {boolean}
     */
    handleCustomErrors(error) {
        if (this.recoverSharedVisitorTypeUnavailable(error)) {
            return true;
        }

        /** @var {string} */
        let errorCode = ''
        let errorText = ''
        if (error && error.response && error.response.data && error.response.data.errors) {
            // Get error code and value
            errorCode = Object.keys(error.response.data.errors)[0];
            errorText = Object.values(error.response.data.errors)[0]
        }

        if (!errorCode) {
            // Unknown error, do default behavior
            return false;
        }

        if (errorCode === 'transaction_requires_action') {
            this.disableNextStageButton();
            const errorData = JSON.parse(errorText);

            const clientSecret = errorData['3DS_meta_data'].client_secret;
            const transactionId = errorData.transaction_id;

            setTimeout(async () => {
                // Let stripe know we need the customer do some extra actions
                const {
                    error,
                } = await this.globalState.stripeInstance.handleNextAction({
                    clientSecret: clientSecret
                });

                if (error) {
                    console.log({errorFromStripeNextAction: error})

                    displayLocalValidationErrorPromise(
                        document.body,
                        'dummy',
                        error.message
                    ).then(async () => {
                        loading();
                        this.clearConfirmationTokenFromPaymentInfo();

                        // We must cancel the transaction first, otherwise the order will remain in the 'requires action' state
                        // until the server considers the transaction expired
                        await this.cancelPendingTransaction(transactionId);
                    });

                } else {
                    this.awaitServerOrderConfirmation()
                        .then();
                }
            });

            return true;
        }

        this.enableNextStageButton();


        return false;
    }

    async cancelPendingTransaction(transactionId) {
        const cancelTransactionRoute = this.domContainer.dataset.cancelPendingTransactionRoute;

        try {
            await Axios.post(cancelTransactionRoute, {
                order_id: this.globalState.orderId,
                transaction_id: transactionId,
            })

            // Force re render and go back to payment stage
            this.globalState.stageManager.clearStageInstance('payment-info');
            this.globalState.stageManager.goToStage('payment-info');

            stopLoading()
        } catch (error) {
            // Something went wrong when cancelling the transaction, order is likely
            // stuck in awaiting payment status until it time outs.
            // Let the user know something went wrong and reload the page.
            stopLoading()

            displayLocalValidationErrorPromise(
                document.body,
                'dummy',
                'Something went wrong please try again later'
            ).then(() => {
                loading()
                window.location.reload();
            })
        }
    }

    clearConfirmationTokenFromPaymentInfo() {
        // Send user back to payment info and clear payment token
        const paymentInfo = {...this.globalState.paymentInfo};

        paymentInfo.stripe_confirmation_token = undefined;
        paymentInfo.stripe_confirmation_token_response = undefined;

        // Update payment info
        this.globalState.setPaymentInfo(paymentInfo);
    }

    async awaitServerOrderConfirmation(withLoading = true) {
        if (withLoading) {
            loading();
        }

        const getOrderStatusRoute = this.domContainer.dataset.getOrderStatusRoute;

        let status;
        let response;

        try {
            response = await Axios.post(
                getOrderStatusRoute,
                {
                    order_id: this.globalState.orderId,
                }
            )

            status = response.data.status;
        } catch (e) {
            console.error('error attempting to retrieve order status', {e});
            status = 'error-in-request'
        }


        if (status === 'paid' || status === 'reservation') {
            // Order has been paid successfully, redirect to receipt view
            this.handleOrderConfirmed(response);
        } else if (status === 'pre_checkout') {
            stopLoading();

            displayLocalValidationErrorPromise(
                document.body,
                'dummy',
                'Something went wrong, please try again'
            ).then(() => {
                this.handleServerOrderConfirmationFailed();
            })
        } else {
            console.log('Order not paid or pre checkout, trying again...', {orderStatus: status});
            // Order must still be processing, try again after a while

            if (this.cancelAwaitingTransactionFlag) {
                console.log('\t...But awaiting was requested to be cancelled');

                // Clear flag
                this.cancelAwaitingTransactionFlag = false;
                return
            }

            setTimeout(() => {
                this.awaitServerOrderConfirmation(withLoading);
            }, 2000)
        }
    }

    handleServerOrderConfirmationFailed() {
        // Somehow, the order is back to pre-checkout state, let the user know and let him try again
        this.clearConfirmationTokenFromPaymentInfo();

        // Force re render and go back to payment stage
        this.globalState.stageManager.clearStageInstance('payment-info');
        this.globalState.stageManager.goToStage('payment-info');
    }

    /**
     * Called after an order that we have been waiting confirmation is confirmed
     *
     * @param response
     */
    handleOrderConfirmed(response) {
        const {receiptRoute} = response.data;
        // Let the user leave without warning
        window.onbeforeunload = undefined;
        window.location.href = receiptRoute;
    }

    backToSeatSelectionStage(error = null) {
        const stageManager = this.globalState.stageManager;
        const errorText = this.getValidationErrorText(error);
        const selectedSeatsSnapshot = (this.globalState.selectedSeats || []).map(selectedSeat => ({...selectedSeat}));

        stageManager.clearStageInstance('seat-select');
        this.globalState.setSelectedSeats(selectedSeatsSnapshot);
        stageManager.goToStage('seat-select');

        waitUntil(
            () => {
                const seatStage = stageManager.stageInstances ? stageManager.stageInstances['seat-select'] : null;

                return seatStage && Array.isArray(seatStage.mapStates) && seatStage.mapStates.length > 0;
            },
            {
                timeoutAfter: 5000,
                msBetweenAttempts: 100,
            }
        ).then(() => {
            try {
                const seatStage = stageManager.stageInstances ? stageManager.stageInstances['seat-select'] : null;

                if (seatStage && typeof seatStage.handleSeatConflicts === 'function') {
                    seatStage.handleSeatConflicts(selectedSeatsSnapshot, errorText);
                } else if (seatStage && typeof seatStage.highlightUnavailableSelectedSeats === 'function') {
                    seatStage.highlightUnavailableSelectedSeats(errorText);
                }
            } catch (highlightError) {
                console.error('Error handling unavailable seat highlight', highlightError);
            }
        }).catch(error => {
            console.error('Timed out waiting for seat stage to highlight unavailable seats', error);
        });
    }

    backToTicketTypeSelectionStage() {
        const stageManager = this.globalState.stageManager;

        if (this.globalState.isPublicMixedCompositionEnabled() === false) {
            this.globalState.setGeneralAdmissionTicketCounts([]);
            this.globalState.setReserveTicketCounts([]);
        }
        this.restoreSelectedDatesFromDateStage();
        this.globalState.recoveringFromCapacityConflict = true;

        const ticketTypeStage = document.querySelector('.stage.seat-type-select');
        if (ticketTypeStage) {
            ticketTypeStage.innerHTML = '';
        }

        stageManager.clearStageInstance('seat-type-select');
        stageManager.goToStage('seat-type-select');
    }

    recoverSharedVisitorTypeUnavailable(error) {
        const recovery = recoverSharedVisitorTypeUnavailable(error, this.globalState);

        if (!recovery) {
            return false;
        }

        displayLocalValidationError(document.body, 'DUMMY', recovery.message);
        this.globalState.recoveringFromCapacityConflict = true;

        const ticketTypeStage = document.querySelector('.stage.seat-type-select');
        if (ticketTypeStage) {
            ticketTypeStage.innerHTML = '';
        }

        this.globalState.stageManager.clearStageInstance('seat-type-select');
        this.globalState.stageManager.goToStage('seat-type-select');

        return true;
    }

    restoreSelectedDatesFromDateStage() {
        const selectedDateIds = Array.from(document.querySelectorAll('.stage.date-select [data-multi-date-toggle][aria-pressed="true"]'))
            .map(toggle => toggle.dataset.eventDateId)
            .filter(eventDateId => !!eventDateId);

        if (selectedDateIds.length === 0 || !this.globalState.parentEvent) {
            return;
        }

        const availableDates = [
            ...(this.globalState.parentEvent.pendingDates || []),
            ...(this.globalState.parentEvent.allDates || []),
        ];
        const datesById = new Map(availableDates.map(eventDate => [eventDate.id, eventDate]));
        const selectedDates = selectedDateIds
            .map(eventDateId => datesById.get(eventDateId))
            .filter(eventDate => !!eventDate);

        if (selectedDates.length > 0) {
            this.globalState.setSelectedDates(selectedDates);
        }
    }

    getValidationErrorText(error = null) {
        try {
            const errors = error && error.response && error.response.data && error.response.data.errors
                ? error.response.data.errors
                : {};

            return Object.values(errors)
                .reduce((messages, errorValue) => messages.concat(errorValue), [])
                .join(' ');
        } catch (parseError) {
            console.error('Error reading validation error text', parseError);
            return '';
        }
    }
}

export {OrderSummaryStage};
